/**
 * Work order page add-ons (Phase 4): the integrity Flags panel and the manual customer email
 * (choose → Preview → Send). Office roles only; the iPad app never loads these.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Flag } from '@wo/shared';
import { api, errorText } from '../lib/api';
import { dateTime } from '../lib/format';
import { Button, Card, ErrorText, Field, Input, Modal, Select, Textarea } from '../components/ui';

export function FlagsPanel({ woId }: { woId: number }) {
  const q = useQuery({ queryKey: ['wo-flags', String(woId)], queryFn: () => api.get<{ flags: Flag[] }>(`/api/work-orders/${woId}/flags`) });
  const flags = q.data?.flags ?? [];
  return (
    <Card title={<span>Flags {flags.length > 0 && <span className="ml-1 rounded-full bg-amber-500 px-2 text-xs text-white">{flags.length}</span>}</span>}>
      {q.isLoading && <p className="text-sm text-slate-500">Checking…</p>}
      <ErrorText error={q.error ? errorText(q.error) : null} />
      {!q.isLoading && !q.error && flags.length === 0 && <p className="text-sm text-green-800">No flags.</p>}
      <ul className="space-y-2">
        {flags.map((f, i) => (
          <li key={`${f.code}-${i}`} className="text-sm">
            <span className="font-semibold text-amber-800">⚑ {f.label}</span>
            <span className="block text-xs text-slate-600">{f.detail}</span>
          </li>
        ))}
      </ul>
      {flags.length > 0 && <p className="mt-3 text-xs text-slate-500">Flags are things to double-check, not proof something is wrong.</p>}
    </Card>
  );
}

interface EmailOptions {
  defaultTo: string[];
  templates: { key: string; label: string; available: boolean; reason: string | null; attachments: string[] }[];
  log: { id: number; to: string; subject: string; template: string; status: 'QUEUED' | 'SENT' | 'FAILED'; error: string | null; sentAt: string | null; createdAt: string }[];
}

interface Preview {
  token: string;
  expiresAt: string;
  to: string[];
  subject: string;
  body: string;
  attachments: { fileId: number; filename: string }[];
}

const STATUS_TEXT = { QUEUED: 'Waiting to send', SENT: 'Sent', FAILED: 'Failed' } as const;

export function EmailCustomerButton({ woId, number }: { woId: number; number: number }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)}>Email customer</Button>
      {open && <EmailCustomerDialog woId={woId} number={number} onClose={() => setOpen(false)} />}
    </>
  );
}

function EmailCustomerDialog({ woId, number, onClose }: { woId: number; number: number; onClose: () => void }) {
  const qc = useQueryClient();
  const opts = useQuery({ queryKey: ['wo-email', String(woId)], queryFn: () => api.get<EmailOptions>(`/api/work-orders/${woId}/email`) });
  const [template, setTemplate] = useState('');
  const [to, setTo] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const toList = (to ?? opts.data?.defaultTo.join(', ') ?? '').split(/[,;\s]+/).filter(Boolean);
  const edited = preview && (subject !== preview.subject || body !== preview.body);

  const doPreview = async (withEdits: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const p = await api.post<Preview>(`/api/work-orders/${woId}/email/preview`, {
        template,
        to: toList,
        ...(withEdits ? { subject, body } : {}),
      });
      setPreview(p);
      setSubject(p.subject);
      setBody(p.body);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ status: 'QUEUED' | 'SENT' | 'FAILED'; error: string | null }>('/api/email/send', { token: preview.token });
      setDone(r.status === 'SENT' ? `Sent to ${preview.to.join(', ')}.` : r.status === 'QUEUED' ? `Queued: ${r.error ?? 'it will be retried shortly'}.` : `Failed: ${r.error}`);
      setPreview(null);
      qc.invalidateQueries({ queryKey: ['wo-email', String(woId)] });
      qc.invalidateQueries({ queryKey: ['wo-audit', String(woId)] });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const chosen = opts.data?.templates.find((t) => t.key === template);

  return (
    <Modal
      title={`Email customer — #${number}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{done ? 'Close' : 'Cancel'}</Button>
          {!preview && !done && <Button onClick={() => doPreview(false)} disabled={!template || !toList.length || busy}>Preview</Button>}
          {preview && edited && <Button variant="secondary" onClick={() => doPreview(true)} disabled={busy}>Update preview</Button>}
          {preview && <Button onClick={send} disabled={busy || Boolean(edited)}>Send</Button>}
        </>
      }
    >
      <ErrorText error={error ?? (opts.error ? errorText(opts.error) : null)} />
      {done && <p className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-800">{done}</p>}
      {!preview && !done && (
        <>
          <Field label="Email" required>
            <Select value={template} onChange={(e) => setTemplate(e.target.value)}>
              <option value="">Choose…</option>
              {opts.data?.templates.map((t) => (
                <option key={t.key} value={t.key} disabled={!t.available}>
                  {t.label}{t.available ? '' : ` — ${t.reason}`}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="To" required hint="Comma-separated. Defaults to the customer's contacts marked “receives email”.">
            <Input value={to ?? opts.data?.defaultTo.join(', ') ?? ''} onChange={(e) => setTo(e.target.value)} placeholder="name@company.com" />
          </Field>
          {chosen && chosen.attachments.length > 0 && <p className="text-xs text-slate-500">Attaches: {chosen.attachments.map((a) => a.replace('_', ' ')).join(', ')} PDF</p>}
        </>
      )}
      {preview && (
        <div className="space-y-3">
          <p className="text-sm"><span className="text-slate-500">To:</span> {preview.to.join(', ')}</p>
          <Field label="Subject"><Input value={subject} onChange={(e) => setSubject(e.target.value)} /></Field>
          <Field label="Message"><Textarea rows={10} value={body} onChange={(e) => setBody(e.target.value)} /></Field>
          {preview.attachments.length > 0 && <p className="text-sm"><span className="text-slate-500">Attached:</span> {preview.attachments.map((a) => a.filename).join(', ')}</p>}
          <p className="text-xs text-slate-500">{edited ? 'You changed the text: Update preview, then Send.' : `Nothing is sent until you press Send. This preview expires ${dateTime(preview.expiresAt)}.`}</p>
        </div>
      )}
      {!preview && (opts.data?.log.length ?? 0) > 0 && (
        <div>
          <h3 className="mb-1 text-sm font-semibold">Emails on this job</h3>
          <ul className="max-h-40 space-y-1 overflow-y-auto text-xs">
            {opts.data!.log.map((l) => (
              <li key={l.id}>
                <span className={l.status === 'FAILED' ? 'font-semibold text-red-700' : l.status === 'SENT' ? 'text-green-800' : 'text-amber-800'}>{STATUS_TEXT[l.status]}</span>{' '}
                · {l.subject} → {l.to} · {dateTime(l.sentAt ?? l.createdAt)}
                {l.error && l.status !== 'SENT' && <span className="block text-red-700">{l.error}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  );
}
